import { t } from "@lingui/macro";
import {
  Accounting_Centre_Type,
  type InputAccountingCentre,
} from "@src/__generated__/graphql";
import {
  Accounting_Account_Type,
  type AccountingAccountBudgetCategory,
  BudgetItemTypeEnum,
  type Currency,
  type DefaultCurrency,
  type InputAccountingAccount,
  type Maybe,
  type Scalars,
  type VatRate,
  type WorkspaceAccountingAccount,
  type WorkspaceAccountingAccountSettings,
  type WorkspaceFinancialSetting,
  type WorkspaceSettingsQuery,
} from "@src/__generated__/urql-graphql";
import type { IOption } from "@src/components/ui-kit";
import { nominate } from "@src/utils/formatters";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { compact, upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { v4 as uuidV4 } from "uuid";

export const EXCHANGE_RATE_DENOMINATION = 10;

type VatRateStateProps = Omit<VatRate, "amount" | "deleted" | "id" | "type"> & {
  amount: string;
};
class VatRateState extends FormState<TToFieldStates<VatRateStateProps>> {
  id?: VatRate["id"];
  internalId: string;
  constructor(
    id?: VatRate["id"],
    fields: VatRateStateProps = {
      amount: "",
      default: false,
    },
  ) {
    super({
      amount: new FieldState(String(fields.amount)).validators(required),
      default: new FieldState(fields.default),
    });
    this.id = id;
    this.internalId = uuidV4();
  }
}

type CurrencyStateProps = Omit<
  Currency,
  "id" | "exchange_rate" | "denomination"
> & {
  isAuto: boolean;
  exchange_rate: string;
};
class CurrencyState extends FormState<TToFieldStates<CurrencyStateProps>> {
  id?: Currency["id"];
  internalId: string;

  constructor(
    id?: Currency["id"],
    fields: Omit<CurrencyStateProps, "isAuto"> = {
      code: "",
      exchange_rate: "1",
    },
  ) {
    super({
      code: new FieldState(fields.code).validators(required),
      exchange_rate: new FieldState(fields.exchange_rate),
      isAuto: new FieldState(fields.exchange_rate === ""),
    });
    this.id = id;
    this.internalId = uuidV4();
  }

  get canBeRemoved() {
    return !this.id;
  }

  get isExchangeRateInputDisabled() {
    return this.$.isAuto.$;
  }

  handleSetAuto(isAuto: boolean) {
    this.$.isAuto.onChange(isAuto);
    if (isAuto) {
      this.$.exchange_rate.onChange("");
    } else {
      this.$.exchange_rate.onChange("1");
    }
  }
}

type AccountingAccountStateProps = Omit<
  WorkspaceAccountingAccount,
  "id" | "accounting_type" | "budget_categories"
>;
class AccountingAccountState extends FormState<
  TToFieldStates<
    AccountingAccountStateProps & {
      budget_categories: string[];
    }
  >
> {
  state: FinancialSettingsState;
  id?: WorkspaceAccountingAccount["id"];
  accountingType: Accounting_Account_Type;
  internalId: string;

  constructor(
    state: FinancialSettingsState,
    accountingType: Accounting_Account_Type,
    id?: WorkspaceAccountingAccount["id"],
    fields: AccountingAccountStateProps = {
      account_number: "",
      title: "",
    },
    budgetCategories: AccountingAccountBudgetCategory[] = [],
  ) {
    super({
      account_number: new FieldState(fields.account_number).validators(
        required,
        (value) =>
          this.state.form.$.billingAccountingAccounts.$.find(
            (a) => a.$.account_number.$ === value && a !== this,
          ) && t`Account number must be unique`,
      ),
      title: new FieldState(fields.title).validators(required),
      budget_categories: new FieldState(
        budgetCategories.map(
          ({ budget_category_id, budget_category_type }) =>
            budget_category_id +
            BUDGET_CATEGORY_VALUE_SEPARATOR +
            budget_category_type,
        ),
      ),
    });
    makeObservable(this, {
      mutationInput: computed,
    });
    this.state = state;
    this.id = id;
    this.internalId = uuidV4();
    this.accountingType = accountingType;
  }

  get mutationInput(): InputAccountingAccount {
    return {
      id: this.id,
      account_number: this.$.account_number.$,
      title: this.$.title.$,
      accounting_type: this.accountingType,
      budget_categories: compact(
        this.$.budget_categories.$.map((category) => {
          const [id, type] = category.split(BUDGET_CATEGORY_VALUE_SEPARATOR);
          if (!id || !type) {
            return null;
          }
          return {
            budget_category_id: id,
            budget_category_type: type,
          };
        }),
      ),
    };
  }
}

const BUDGET_CATEGORY_VALUE_SEPARATOR = "-";

type CostCenterStateProps = {
  type: Accounting_Centre_Type | undefined;
  cost_center: string;
  activity: string;
  id: Scalars["ID"]["input"];
};

class CostCenterState extends FormState<TToFieldStates<CostCenterStateProps>> {
  internalId: string;

  constructor(
    fields: CostCenterStateProps = {
      id: "",
      type: undefined,
      cost_center: "",
      activity: "",
    },
  ) {
    super({
      id: new FieldState(fields.id),
      type: new FieldState(fields.type).validators(required),
      cost_center: new FieldState(fields.cost_center).validators(required),
      activity: new FieldState(fields.activity).validators(required),
    });
    makeObservable(this, {
      mutationInput: computed,
    });
    this.internalId = uuidV4();
  }

  get mutationInput(): InputAccountingCentre | undefined {
    if (!this.$.type.$) return undefined;

    return {
      id: this.$.id.$,
      type: this.$.type.$,
      centre: this.$.cost_center.$,
      title: this.$.activity.$,
    };
  }
}

type AccountingVatClassificationStateProps = {
  id: Scalars["ID"]["input"] | undefined;
  type: Accounting_Centre_Type | undefined;
  vat_rate_id: string | undefined;
  centre: string;
  title: string;
};

class AccountingVatClassificationState extends FormState<
  TToFieldStates<AccountingVatClassificationStateProps>
> {
  internalId: string;

  constructor(
    fields: AccountingVatClassificationStateProps = {
      id: undefined,
      type: Accounting_Centre_Type.Expense,
      vat_rate_id: undefined,
      centre: "",
      title: "",
    },
  ) {
    super({
      id: new FieldState(fields.id),
      type: new FieldState(fields.type).validators(required),
      vat_rate_id: new FieldState(fields.vat_rate_id).validators(required),
      centre: new FieldState(fields.centre).validators(required),
      title: new FieldState(fields.title).validators(required),
    });
    makeObservable(this, {
      mutationInput: computed,
    });
    this.internalId = uuidV4();
  }

  get mutationInput() {
    if (!this.$.type.$ || !this.$.vat_rate_id.$) return undefined;

    return {
      id: this.$.id.$,
      type: this.$.type.$,
      vat_rate_id: this.$.vat_rate_id.$,
      centre: this.$.centre.$,
      title: this.$.title.$,
    };
  }
}

export class FinancialSettingsState {
  @observable vatRateToDelete: VatRateState | undefined = undefined;
  @observable currencyOptions: IOption[] = [];
  budgetCategoryOptions: IOption[] = [];
  @observable billingAccountingAccountToDelete:
    | AccountingAccountState
    | undefined = undefined;
  @observable expensesAccountingAccountToDelete:
    | AccountingAccountState
    | undefined = undefined;
  @observable costCenterToDelete: CostCenterState | undefined = undefined;
  @observable accountingVatClassificationToDelete:
    | AccountingVatClassificationState
    | undefined = undefined;
  readonly form = new FormState({
    vatRates: new FormState<Array<VatRateState>>([]),
    billingAccountingAccounts: new FormState<Array<AccountingAccountState>>([]),
    expensesAccountingAccounts: new FormState<Array<AccountingAccountState>>(
      [],
    ),
    currencies: new FormState<Array<CurrencyState>>([]),
    costCenters: new FormState<Array<CostCenterState>>([]),
    accountingVatClassifications: new FormState<
      Array<AccountingVatClassificationState>
    >([]),
    showUnitPrice: new FieldState(false),
    showBillingAccounting: new FieldState(false),
    show_full_bank_account_information: new FieldState(false),
    attachmentNameOutgoingInvoice: new FieldState(""),
    attachmentNameOutgoingProforma: new FieldState(""),
    attachmentNamePaymentReceipt: new FieldState(""),
  });

  constructor() {
    makeObservable(this);
  }

  init(
    financialSettings: WorkspaceFinancialSetting,
    accountingAccountSettings: WorkspaceAccountingAccountSettings,
    ourWorkBudgetCategories: NonNullable<
      WorkspaceSettingsQuery["ourWorkBudgetCategories"]
    >,
    expenseBudgetCategories: NonNullable<
      WorkspaceSettingsQuery["expenseBudgetCategories"]
    >,
    defaultCurrencies: Maybe<DefaultCurrency>[],
    costCenters: WorkspaceSettingsQuery["workspaceAccountingCentreSetting"],
    accountingVatClassificationSetting: WorkspaceSettingsQuery["workspaceAccountingVatClassificationSetting"],
  ) {
    this.form.$.vatRates = new FormState(
      financialSettings.vat_rates
        .filter((vatRate) => !vatRate.deleted)
        .map((vatRate) => {
          const humanReadableVatRate = {
            ...vatRate,
            amount: String(vatRate.amount / 100),
          };
          return new VatRateState(vatRate.id, humanReadableVatRate);
        }),
    );
    this.currencyOptions = compact(
      defaultCurrencies.map((currency) => {
        return currency
          ? {
              value: currency.code,
              label: currency.code,
            }
          : null;
      }),
    );

    this.budgetCategoryOptions = [];
    for (const category of ourWorkBudgetCategories) {
      this.budgetCategoryOptions.push({
        value:
          category.id +
          BUDGET_CATEGORY_VALUE_SEPARATOR +
          BudgetItemTypeEnum.OurWorkBudgetItem,
        label: category.name,
      });
    }

    for (const category of expenseBudgetCategories) {
      this.budgetCategoryOptions.push({
        value:
          category.id +
          BUDGET_CATEGORY_VALUE_SEPARATOR +
          BudgetItemTypeEnum.ExpenseBudgetItem,
        label: category.name,
      });
    }

    this.form.$.currencies = new FormState(
      financialSettings.currencies.map(
        (currency) =>
          new CurrencyState(currency.id, {
            code: currency.code,
            exchange_rate: currency.exchange_rate
              ? nominate(
                  currency.exchange_rate,
                  EXCHANGE_RATE_DENOMINATION,
                ).toString()
              : "",
          }),
      ),
    );
    this.form.$.billingAccountingAccounts = new FormState(
      accountingAccountSettings.accounting_accounts
        .filter(
          (account) =>
            account.accounting_type === Accounting_Account_Type.Invoice,
        )
        .map(
          (account) =>
            new AccountingAccountState(
              this,
              account.accounting_type,
              account.id,
              account,
              account.budget_categories ?? [],
            ),
        ),
    );
    this.form.$.expensesAccountingAccounts = new FormState(
      accountingAccountSettings.accounting_accounts
        .filter(
          (account) =>
            account.accounting_type === Accounting_Account_Type.Expense,
        )
        .map(
          (account) =>
            new AccountingAccountState(
              this,
              account.accounting_type,
              account.id,
              account,
            ),
        ),
    );

    this.form.$.showUnitPrice = new FieldState(
      financialSettings.default_show_unit_price_on_invoices,
    );
    this.form.$.showBillingAccounting = new FieldState(
      financialSettings.default_show_billing_accounting_on_invoices,
    );

    const templates = financialSettings.invoice_attachment_name_templates;
    this.form.$.attachmentNameOutgoingInvoice = new FieldState(
      templates?.outgoing_invoice ?? "",
    );
    this.form.$.attachmentNameOutgoingProforma = new FieldState(
      templates?.outgoing_proforma ?? "",
    );
    this.form.$.attachmentNamePaymentReceipt = new FieldState(
      templates?.payment_receipt ?? "",
    );

    this.form.$.costCenters = new FormState(
      costCenters.accounting_centres
        .filter((center) => !center.deleted_at)
        .map(
          (center) =>
            new CostCenterState({
              id: center.id,
              type: center.type,
              cost_center: center.centre,
              activity: center.title,
            }),
        ),
    );

    this.form.$.accountingVatClassifications = new FormState(
      accountingVatClassificationSetting.accounting_vat_classifications
        .filter((classification) => !classification.deleted_at)
        .map(
          (classification) =>
            new AccountingVatClassificationState({
              id: classification.id,
              type: classification.type,
              vat_rate_id: classification.vat_rate_id ?? undefined,
              centre: classification.centre,
              title: classification.title,
            }),
        ),
    );
  }

  @computed get nonSelectedCurrencyOptions(): IOption[] {
    return this.currencyOptions.filter(({ value }) =>
      this.form.$.currencies.$.every((currency) => currency.$.code.$ !== value),
    );
  }

  @computed get costCenterOptions(): IOption[] {
    return Object.entries(Accounting_Centre_Type).map(([key, value]) => ({
      value: value,
      label: upperFirst(key.toLowerCase()),
    }));
  }

  @computed get vatRateOptions(): IOption[] {
    return this.form.$.vatRates.$.map((vatRate) => ({
      value: vatRate.id ?? "",
      label: vatRate.$.amount.$ + "%",
    }));
  }

  addNewVatRate() {
    const newLength = this.form.$.vatRates.$.push(new VatRateState());

    // If we just added our very first VAT rate, then no VAT rate is default currently.
    // We need to make the new one default.
    if (newLength === 1) {
      this.form.$.vatRates.$[0].$.default.onChange(true);
    }
  }

  addNewCurrency() {
    this.form.$.currencies.$.push(new CurrencyState());
  }

  @action.bound removeCurrency(idToRemove: string) {
    this.form.$.currencies = new FormState(
      this.form.$.currencies.$.filter(
        (currency) => currency.internalId !== idToRemove,
      ),
    );
  }

  @action.bound requestVatRateRemoval(vatRate: VatRateState) {
    this.vatRateToDelete = vatRate;
  }

  @action.bound removeVatRate(vatRate: VatRateState) {
    // Make sure to reassign default flag to another VAT rate if the one being deleted was default.
    if (vatRate.$.default.$) {
      this.form.$.vatRates.$.find((v) => v !== vatRate)?.$.default.onChange(
        true,
      );
    }

    this.form.$.vatRates = new FormState(
      this.form.$.vatRates.$.filter((v) => v !== vatRate),
    );

    this.vatRateToDelete = undefined;
  }

  makeDefault(vatRate: VatRateState) {
    this.form.$.vatRates.$.forEach((v) => {
      v.$.default.onChange(v === vatRate);
    });
  }

  addNewBillingAccountingAccount() {
    this.form.$.billingAccountingAccounts.$.push(
      new AccountingAccountState(this, Accounting_Account_Type.Invoice),
    );
  }

  addNewExpensesAccountingAccount() {
    this.form.$.expensesAccountingAccounts.$.push(
      new AccountingAccountState(this, Accounting_Account_Type.Expense),
    );
  }

  @action.bound requestBillingAccountingAccountRemoval(
    account: AccountingAccountState,
  ) {
    this.billingAccountingAccountToDelete = account;
  }

  @action.bound removeBillingAccountingAccount(
    account: AccountingAccountState,
  ) {
    this.form.$.billingAccountingAccounts = new FormState(
      this.form.$.billingAccountingAccounts.$.filter((a) => a !== account),
    );

    this.billingAccountingAccountToDelete = undefined;
  }

  @action.bound requestExpensesAccountingAccountRemoval(
    account: AccountingAccountState,
  ) {
    this.expensesAccountingAccountToDelete = account;
  }

  @action.bound removeExpensesAccountingAccount(
    account: AccountingAccountState,
  ) {
    this.form.$.expensesAccountingAccounts = new FormState(
      this.form.$.expensesAccountingAccounts.$.filter((a) => a !== account),
    );

    this.expensesAccountingAccountToDelete = undefined;
  }

  getFilteredCurrencyOptions(codeToFilterOut: Currency["code"]): IOption[] {
    return [
      { value: codeToFilterOut, label: codeToFilterOut },
      ...this.nonSelectedCurrencyOptions,
    ];
  }

  @action.bound addNewCostCenter() {
    this.form.$.costCenters.$.push(new CostCenterState());
  }

  @action.bound requestCostCenterRemoval(costCenter: CostCenterState) {
    this.costCenterToDelete = costCenter;
  }

  @action.bound removeCostCenter(costCenter: CostCenterState) {
    const index = this.form.$.costCenters.$.findIndex(
      (cc) => cc.internalId === costCenter.internalId,
    );
    if (index === -1) return;
    this.form.$.costCenters.$.splice(index, 1);
    this.costCenterToDelete = undefined;
  }

  @action.bound
  addNewAccountingVatClassification() {
    this.form.$.accountingVatClassifications.$.push(
      new AccountingVatClassificationState(),
    );
  }

  @action.bound
  requestAccountingVatClassificationRemoval(
    classification: AccountingVatClassificationState,
  ) {
    this.accountingVatClassificationToDelete = classification;
  }

  @action.bound
  removeAccountingVatClassification(
    classification: AccountingVatClassificationState,
  ) {
    const index = this.form.$.accountingVatClassifications.$.findIndex(
      (c) => c.internalId === classification.internalId,
    );
    if (index === -1) return;
    this.form.$.accountingVatClassifications.$.splice(index, 1);
    this.accountingVatClassificationToDelete = undefined;
  }
}
